import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice.js';
import { toast } from 'react-hot-toast';
import { Edit, Trash2, X, Save, Package } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout.jsx';
import ManifestSection from '../components/dashboard/ShipmentForm/ManifestSection.jsx';
import DetailsSection from '../components/dashboard/ShipmentForm/DetailsSection.jsx';
import EventSection from '../components/dashboard/ShipmentForm/EventSection.jsx';
import SelectionModals from '../components/dashboard/ShipmentForm/SelectionModals.jsx';
import { GET_EDITABLE_MANIFESTS_QUERY, ORIGINS_QUERY, DESTINATIONS_QUERY, SUPPLIERS_QUERY, USERS_QUERY } from '../services/graphql/queries.js';
import { UPDATE_COMPLETE_MANIFEST_MUTATION, DELETE_COMPLETE_MANIFEST_MUTATION } from '../services/graphql/mutations.js';

const EditManifest = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  // Estado
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModals, setShowModals] = useState({
    origins: false,
    destinations: false,
    suppliers: false,
    consignees: false,
    ff: false
  });
  const [currentDetailIndex, setCurrentDetailIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [manifestToDelete, setManifestToDelete] = useState(null);

  // GraphQL Queries
  const { data: manifestsData, loading, refetch } = useQuery(GET_EDITABLE_MANIFESTS_QUERY, {
    fetchPolicy: 'cache-and-network'
  });
  const { data: originsData } = useQuery(ORIGINS_QUERY);
  const { data: destinationsData } = useQuery(DESTINATIONS_QUERY);
  const { data: suppliersData } = useQuery(SUPPLIERS_QUERY);
  const { data: usersData } = useQuery(USERS_QUERY);

  // GraphQL Mutations
  const [updateManifest] = useMutation(UPDATE_COMPLETE_MANIFEST_MUTATION, {
    onCompleted: () => {
      toast.success('Manifiesto actualizado exitosamente');
      setIsEditing(false);
      setSelectedManifest(null);
      refetch();
    },
    onError: (error) => {
      console.error('GraphQL Error:', error);

      // Extraer mensaje específico de validación
      let errorMessage = 'Error al actualizar el manifiesto';

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];

        if (graphQLError.extensions?.code === 'VALIDATION_ERROR') {
          errorMessage = `⚠️ Error de validación: ${graphQLError.message}`;
        } else {
          errorMessage = graphQLError.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        duration: 6000, // Más tiempo para leer el mensaje
        position: 'top-center'
      });

      setIsSubmitting(false);
    }
  });

  const [deleteManifest] = useMutation(DELETE_COMPLETE_MANIFEST_MUTATION, {
    onCompleted: () => {
      toast.success('Manifiesto eliminado exitosamente');
      setShowDeleteConfirm(false);
      setManifestToDelete(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar el manifiesto');
    }
  });

  // Función utilitaria para limpiar espacios en blanco
  const trimFormData = (data) => {
    if (typeof data === 'string') {
      return data.trim();
    }

    if (Array.isArray(data)) {
      return data.map(item => trimFormData(item));
    }

    if (data !== null && typeof data === 'object') {
      const trimmedData = {};
      for (const [key, value] of Object.entries(data)) {
        trimmedData[key] = trimFormData(value);
      }
      return trimmedData;
    }

    return data;
  };

  // Manejar selección de manifiesto para editar
  const handleEdit = (manifest) => {
    setSelectedManifest(manifest);

    // Obtener el evento no aprobado si existe
    const unapprovedEvent = manifest.events.find(e => !e.approved);

    setFormData({
      shipment: {
        manifest: manifest.manifest,
        reference: manifest.reference,
        originId: manifest.originId,
        destinationId: manifest.destinationId,
        driver: manifest.driver || '',
        driver_passport: manifest.driver_passport || '',
        driver_license: manifest.driver_license || '',
        driver_phone: manifest.driver_phone || '',
        notes: manifest.notes || ''
      },
      details: manifest.details.map(detail => {
        const hasAdditionalConsignees = Boolean(detail.additional_consignees);

        // Detectar si es modo manual (sin consigneeId) o Transportista (con consigneeId).
        // 🔒 La detección de Transportista se hace por los DATOS del detail
        // (consigneeId + additional_consignees), no por el rol en usersData:
        // un consignatario desactivado pierde su rol (deactivateUser pone
        // roleId=null) y la detección por rol borraba additional_consignees
        // silenciosamente al guardar (hallazgo A-2, AuditoriaFinal_Sesion29.md).
        // El invariante de negocio garantiza que solo los Transportistas
        // pueden tener additional_consignees con consigneeId presente.
        const isModoManual = hasAdditionalConsignees && !detail.consigneeId;
        const isTransportistaConsignee = Boolean(detail.consigneeId && hasAdditionalConsignees);

        return {
          bill_of_lading: detail.bill_of_lading,
          commercial_invoice: detail.commercial_invoice,
          // Solo vaciar consigneeId si es MODO MANUAL (sin consigneeId en BD)
          consigneeId: isModoManual ? '' : (detail.consigneeId || ''),
          suppliers: detail.suppliers || [],
          ff: detail.ff,
          seller: detail.seller || null,
          cif: detail.cif || false,
          additional_consignees: detail.additional_consignees || '',
          isManualConsignee: isModoManual,
          isTransportistaConsignee
        };
      }),
      event: unapprovedEvent ? {
        status_detail: unapprovedEvent.status_detail,
        status_date: unapprovedEvent.status_date
      } : {
        status_detail: '',
        status_date: new Date().toISOString()
      }
    });

    setIsEditing(true);
  };

  // Validación del formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.shipment.manifest.trim()) {
      newErrors.manifest = 'El número de manifiesto es requerido';
    }

    if (!formData.shipment.reference.trim()) {
      newErrors.reference = 'La referencia es requerida';
    }

    if (!formData.shipment.originId) {
      newErrors.originId = 'El origen es requerido';
    }

    if (!formData.shipment.destinationId) {
      newErrors.destinationId = 'El destino es requerido';
    }

    if (!formData.shipment.driver.trim()) {
      newErrors.driver = 'El conductor es requerido';
    }

    // Los errores de detalles se acumulan en una lista (L-6, AuditoriaFinal_Sesion29.md):
    // antes cada validación sobreescribía la anterior y solo se mostraba la última.
    if (!formData.details || formData.details.length === 0) {
      newErrors.details = 'Se requiere al menos un detalle de envío';
    } else {
      const detailErrors = [];

      // Verificar que al menos un detalle esté completo
      const hasCompleteDetail = formData.details.some(detail => {
        const hasConsignee = detail.consigneeId ||
          (detail.isManualConsignee && detail.additional_consignees?.trim()) ||
          (detail.isTransportistaConsignee && detail.additional_consignees?.trim());
        const hasSuppliers = detail.suppliers && detail.suppliers.length > 0;
        return detail.bill_of_lading.trim() &&
          detail.commercial_invoice.trim() &&
          hasConsignee &&
          hasSuppliers;
      });

      if (!hasCompleteDetail) {
        detailErrors.push('Se requiere al menos un detalle completo');
      }

      // Validar que no haya detalles parcialmente llenos
      formData.details.forEach((detail, index) => {
        const bl = detail.bill_of_lading?.trim() || '';
        const ci = detail.commercial_invoice?.trim() || '';
        const hasConsignee = detail.consigneeId ||
          (detail.isManualConsignee && detail.additional_consignees?.trim()) ||
          (detail.isTransportistaConsignee && detail.additional_consignees?.trim());
        const hasSuppliers = detail.suppliers && detail.suppliers.length > 0;

        // Contar cuántos campos están llenos
        const filledFields = [bl, ci, hasConsignee, hasSuppliers].filter(f => f).length;

        // Si hay campos llenos pero no todos (parcialmente lleno)
        if (filledFields > 0 && filledFields < 4) {
          detailErrors.push(`Detalle #${index + 1}: Todos los campos obligatorios deben estar completos (BL, Factura, Consignatario, al menos 1 Proveedor)`);
        }

        // Validar campos manuales de consignees si están activos
        if (detail.isManualConsignee) {
          const trimmedConsignees = detail.additional_consignees?.trim() || '';
          if (!trimmedConsignees) {
            detailErrors.push(`Detalle #${index + 1}: Debe digitar al menos un consignatario`);
          } else {
            // Validar que al menos una línea tenga contenido
            const lines = trimmedConsignees.split('\n').filter(line => line.trim());
            if (lines.length === 0) {
              detailErrors.push(`Detalle #${index + 1}: Debe digitar al menos un consignatario con contenido válido`);
            }
          }
        }

        // Validar Transportista: additional_consignees Y FF son OBLIGATORIOS
        if (detail.isTransportistaConsignee) {
          const trimmedConsignees = detail.additional_consignees?.trim() || '';
          if (!trimmedConsignees) {
            detailErrors.push(`Detalle #${index + 1}: Debe digitar consignatarios adicionales cuando selecciona un Transportista como consignatario.`);
          } else if (!detail.ff) {
            detailErrors.push(`Detalle #${index + 1}: FF (Freight Forwarder) es obligatorio cuando selecciona un Transportista como consignatario.`);
          }
        }
      });

      // Validar duplicados en Bill of Lading
      const blValues = formData.details
        .map(d => d.bill_of_lading?.trim())
        .filter(bl => bl); // Solo los que tienen valor

      const duplicateBLs = blValues.filter((bl, index) =>
        blValues.indexOf(bl) !== index
      );

      if (duplicateBLs.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateBLs)];
        detailErrors.push(`❌ No se permiten conocimientos (BL) duplicados. Revise los siguientes: "${uniqueDuplicates.join('", "')}"`);
      }

      // Validar duplicados en Commercial Invoice
      const ciValues = formData.details
        .map(d => d.commercial_invoice?.trim())
        .filter(ci => ci); // Solo los que tienen valor

      const duplicateCIs = ciValues.filter((ci, index) =>
        ciValues.indexOf(ci) !== index
      );

      if (duplicateCIs.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateCIs)];
        detailErrors.push(`❌ No se permiten números de factura duplicados. Revise los siguientes: "${uniqueDuplicates.join('", "')}"`);
      }

      if (detailErrors.length > 0) {
        newErrors.details = detailErrors.join('\n');
      }
    }

    if (formData.event && formData.event.status_detail && !formData.event.status_detail.trim()) {
      newErrors.event = 'El estado es requerido si se proporciona';
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;

    // Mostrar toast si hay errores
    if (hasErrors) {
      toast.error('⚠️ Verifica todo el manifiesto porque hay errores');
    }

    return !hasErrors;
  };

  // Manejo de cambios en el formulario
  const handleChange = (section, field, value, index = null) => {
    if (section === 'details' && index !== null) {
      const newDetails = [...formData.details];
      newDetails[index] = { ...newDetails[index], [field]: value };

      if (field === 'ff' && !value) {
        newDetails[index].cif = false;
      }

      setFormData(prev => ({ ...prev, details: newDetails }));

      // Limpiar error de details cuando se modifica un BL o factura
      if ((field === 'bill_of_lading' || field === 'commercial_invoice') && errors.details) {
        setErrors(prev => ({ ...prev, details: '' }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Remover supplier del array
  const handleRemoveSupplier = (detailIndex, supplierId) => {
    const newDetails = [...formData.details];
    const currentSuppliers = newDetails[detailIndex].suppliers || [];

    // Validar que no sea el último
    if (currentSuppliers.length === 1) {
      toast.error('Debe haber al menos un proveedor');
      return;
    }

    newDetails[detailIndex].suppliers = currentSuppliers.filter(s => s.id !== supplierId);
    setFormData(prev => ({ ...prev, details: newDetails }));
  };

  // Agregar nuevo detalle
  const addDetail = () => {
    setFormData(prev => ({
      ...prev,
      details: [
        ...prev.details,
        {
          bill_of_lading: '',
          commercial_invoice: '',
          consigneeId: '',
          suppliers: [],
          ff: null,
          cif: false,
          additional_consignees: '',
          seller: null,
          isManualConsignee: false,
          isTransportistaConsignee: false
        }
      ]
    }));
  };

  // Eliminar detalle
  const removeDetail = (index) => {
    if (formData.details.length > 1) {
      const newDetails = formData.details.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, details: newDetails }));
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const trimmedFormData = trimFormData(formData);

    // 🔒 VALIDACIÓN ADICIONAL: FF obligatorio para Transportista con additional_consignees
    const requiresFF = trimmedFormData.details.filter(d =>
      d.isManualConsignee || (d.isTransportistaConsignee && d.additional_consignees?.trim())
    );
    if (requiresFF.length > 0) {
      const missingFF = requiresFF.some(detail => !detail.ff);
      if (missingFF) {
        toast.error('Debe seleccionar un FF si digita los consignatarios manualmente o si selecciona un Transportista con consignatarios adicionales');
        setIsSubmitting(false);
        return;
      }
    }

    // Limpiar detalles: consigneeId = null cuando isManualConsignee, y viceversa
    const cleanedDetails = trimmedFormData.details
      .map(detail => ({
        bill_of_lading: detail.bill_of_lading,
        commercial_invoice: detail.commercial_invoice,
        consigneeId: detail.isManualConsignee ? null : (detail.consigneeId || null),
        supplierIds: detail.suppliers ? detail.suppliers.map(s => s.id) : [],
        ff: detail.ff,
        cif: detail.cif,
        // 🔒 Si es manual O Transportista, enviar additional_consignees
        additional_consignees: (detail.isManualConsignee || detail.isTransportistaConsignee)
          ? detail.additional_consignees
          : null,
        seller: detail.seller || null
      }))
      .filter(detail => {
        // Filtrar detalles completamente vacíos
        const hasBL = detail.bill_of_lading?.trim();
        const hasCI = detail.commercial_invoice?.trim();
        const hasConsignee = detail.consigneeId || detail.additional_consignees?.trim();
        const hasSuppliers = detail.supplierIds && detail.supplierIds.length > 0;

        // Solo incluir si tiene al menos UN campo con datos
        return hasBL || hasCI || hasConsignee || hasSuppliers;
      });

    // Validar que haya al menos un detalle válido
    if (cleanedDetails.length === 0) {
      toast.error('⚠️ Debe llenar al menos un detalle antes de actualizar');
      setIsSubmitting(false);
      return;
    }

    const inputData = {
      oldManifest: selectedManifest.manifest,
      shipment: trimmedFormData.shipment,
      details: cleanedDetails,
      event: trimmedFormData.event.status_detail ? {
        status_detail: trimmedFormData.event.status_detail,
        status_date: new Date(trimmedFormData.event.status_date)
      } : null
    };

    try {
      await updateManifest({
        variables: {
          input: inputData
        }
      });
    } catch (error) {
      console.error('Error updating manifest:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancelar edición
  const handleCancel = () => {
    setIsEditing(false);
    setSelectedManifest(null);
    setFormData(null);
    setErrors({});
  };

  // Manejar eliminación
  const handleDeleteClick = (manifest) => {
    setManifestToDelete(manifest);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!manifestToDelete) return;

    try {
      await deleteManifest({
        variables: {
          manifest: manifestToDelete.manifest
        }
      });
    } catch (error) {
      console.error('Error deleting manifest:', error);
    }
  };

  // Renderizar tabla de manifiestos
  const renderManifestsList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!manifestsData?.getEditableManifests || manifestsData.getEditableManifests.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No hay manifiestos editables disponibles
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manifest
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reference
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Origen
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Destino
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conductor
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {manifestsData.getEditableManifests.map((manifest) => (
              <tr key={manifest.manifest} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {manifest.manifest}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {manifest.reference}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {manifest.origin_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {manifest.destination_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {manifest.driver || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleEdit(manifest)}
                    className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteClick(manifest)}
                    className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Renderizar formulario de edición
  const renderEditForm = () => {
    if (!formData) return null;

    return (
      <div className="space-y-6">
        <ManifestSection
          data={formData.shipment}
          errors={errors}
          onChange={(field, value) => handleChange('shipment', field, value)}
          onOpenModal={(modalType) => setShowModals(prev => ({ ...prev, [modalType]: true }))}
          originsData={originsData}
          destinationsData={destinationsData}
        />

        <DetailsSection
          data={formData.details}
          errors={errors}
          onChange={(field, value, index) => handleChange('details', field, value, index)}
          onAddDetail={addDetail}
          onRemoveDetail={removeDetail}
          onRemoveSupplier={handleRemoveSupplier}
          onOpenModal={(modalType, index) => {
            setCurrentDetailIndex(index);
            setShowModals(prev => ({ ...prev, [modalType]: true }));
          }}
          suppliersData={suppliersData}
          usersData={usersData}
        />

        <EventSection
          data={formData.event}
          errors={errors}
          onChange={(field, value) => handleChange('event', field, value)}
        />

        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Cancelar</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{isSubmitting ? 'Guardando...' : 'Guardar Cambios'}</span>
          </button>
        </div>

        {/* Modales */}
        <SelectionModals
          showModals={showModals}
          onCloseModal={(modalType) => {
            setShowModals(prev => ({ ...prev, [modalType]: false }));
            setCurrentDetailIndex(null);
          }}
          onSelect={(modalType, selectedItem) => {
            if (modalType === 'origins') {
              handleChange('shipment', 'originId', selectedItem.id);
            } else if (modalType === 'destinations') {
              handleChange('shipment', 'destinationId', selectedItem.id);
            } else if (modalType === 'suppliers' && currentDetailIndex !== null) {
              // Agregar supplier al array (no reemplazar)
              const newDetails = [...formData.details];
              const currentSuppliers = newDetails[currentDetailIndex].suppliers || [];

              // Validar que no esté duplicado
              if (currentSuppliers.find(s => s.id === selectedItem.id)) {
                toast.error('Este proveedor ya fue agregado');
                return;
              }

              newDetails[currentDetailIndex] = {
                ...newDetails[currentDetailIndex],
                suppliers: [...currentSuppliers, selectedItem],
              };
              setFormData(prev => ({ ...prev, details: newDetails }));

              // NO cerrar modal para permitir agregar más
              // Modal permanece abierto
            } else if (modalType === 'consignees' && currentDetailIndex !== null) {
              if (selectedItem.id === 'manual') {
                // Activar modo manual para consignees
                const newDetails = [...formData.details];
                newDetails[currentDetailIndex] = {
                  ...newDetails[currentDetailIndex],
                  consigneeId: '',
                  isManualConsignee: true,
                  isTransportistaConsignee: false,
                  additional_consignees: '',
                  cif: false // 🔒 Desactivar CIF cuando se activa modo manual
                };
                setFormData(prev => ({ ...prev, details: newDetails }));
              } else {
                // Selección normal - detectar si es Transportista o Cliente
                const selectedUser = usersData?.users?.find(u => u.id === selectedItem.id);
                const isTransportista = selectedUser?.role?.role_name === 'Transportista';

                const newDetails = [...formData.details];
                newDetails[currentDetailIndex] = {
                  ...newDetails[currentDetailIndex],
                  consigneeId: selectedItem.id,
                  isManualConsignee: false,
                  isTransportistaConsignee: isTransportista,
                  // Si es Cliente, limpiar additional_consignees
                  additional_consignees: isTransportista ? newDetails[currentDetailIndex].additional_consignees : ''
                };
                setFormData(prev => ({ ...prev, details: newDetails }));
              }
            } else if (modalType === 'ff' && currentDetailIndex !== null) {
              handleChange('details', 'ff', selectedItem?.id || null, currentDetailIndex);
            } else if (modalType === 'seller' && currentDetailIndex !== null) {
              handleChange('details', 'seller', selectedItem?.id || null, currentDetailIndex);
            }
          }}
          originsData={originsData}
          destinationsData={destinationsData}
          suppliersData={suppliersData}
          usersData={usersData}
        />
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Editar Manifiesto</h1>
              <p className="text-gray-600">
                {isEditing ? 'Editando manifiesto seleccionado' : 'Selecciona un manifiesto para editar'}
              </p>
            </div>
          </div>
          {isEditing && (
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Volver a la lista
            </button>
          )}
        </div>

        {/* Notas informativas */}
        {!isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Información importante:</h4>
            <ul className="text-xs text-blue-800 space-y-1.5">
              <li>• <strong>Manifiestos editables:</strong> Solo se muestran manifiestos en tránsito con máximo un evento sin aprobar o sin eventos registrados.</li>
              <li>• <strong>Para aprobar y enviar correos:</strong> Una vez editado, ve al Dashboard → Estados Pendientes para aprobar el evento y notificar a los destinatarios.</li>
              <li>• <strong>Validación automática:</strong> El sistema verifica que no existan manifiestos o referencias duplicadas antes de guardar.</li>
              <li>• <strong>Conocimientos y facturas únicos:</strong> No se permiten conocimientos (BL) ni números de factura duplicados dentro del mismo manifiesto.</li>
              <li>• <strong>Actualización en cascada:</strong> Si cambias el número de manifiesto, se actualizará automáticamente en todas las tablas relacionadas.</li>
            </ul>
          </div>
        )}

        {/* Contenido */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {isEditing ? renderEditForm() : renderManifestsList()}
        </div>

        {/* Modal de confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirmar eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que deseas eliminar el manifiesto <strong>{manifestToDelete?.manifest}</strong>?
                Esta acción eliminará todos los detalles y eventos asociados.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setManifestToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditManifest;